import { Field, ID, ObjectType, registerEnumType } from 'type-graphql'
import {
  Column,
  CreateDateColumn,
  Entity,
  Index,
  ManyToOne,
  PrimaryGeneratedColumn,
  RelationId,
  UpdateDateColumn,
  DeleteDateColumn,
  VersionColumn
} from 'typeorm'

import { Role, User } from '@things-factory/auth-base'
import { ApprovalLineItem } from '@things-factory/organization'
import { Domain, ScalarObject } from '@things-factory/shell'
import { AssigneeItem } from '@things-factory/worklist'
import { Scenario } from '@things-factory/integration-base'

import { DataKeySet } from '../data-key-set/data-key-set.js'
import { DataItem } from './data-item-type.js'

export enum DataSetEntryType {
  Generated = 'generated',
  Board = 'board',
  CustomElement = 'custom-element',
  Page = 'page',
  ExternalURL = 'external',
  Jasper = 'jasper',
  Shiny = 'shiny'
}

registerEnumType(DataSetEntryType, {
  name: 'DataSetEntryType',
  description: 'entry type enumeration for data-set'
})

export enum DataSetMonitorType {
  Generated = 'generated',
  Board = 'board',
  CustomElement = 'custom-element',
  Page = 'page',
  ExternalURL = 'external'
}

registerEnumType(DataSetMonitorType, {
  name: 'DataSetMonitorType',
  description: 'monitor type enumeration for data-set'
})

export enum DataSetReportType {
  Generated = 'generated',
  Board = 'board',
  CustomElement = 'custom-element',
  Page = 'page',
  ExternalURL = 'external'
}

registerEnumType(DataSetReportType, {
  name: 'DataSetReportType',
  description: 'report type enumeration for data-set'
})

export enum DataSetSummaryPeriodType {
  Hour = 'hour',
  WorkShift = 'work-shift',
  WorkDate = 'work-date',
  Day = 'day'
}

registerEnumType(DataSetSummaryPeriodType, {
  name: 'DataSetSummaryPeriodType',
  description: 'summary period type enumeration for data-set'
})

@ObjectType()
export class DataSetState {
  @Field()
  id: string

  @Field()
  scheduleId: string

  @Field()
  state: string

  @Field()
  startedAt: Date

  @Field()
  nextSchedule: Date
}

@Entity()
@Index('ix_data_set_0', (dataSet: DataSet) => [dataSet.domain, dataSet.name], { unique: true })
@ObjectType({ description: 'Entity for DataSet' })
export class DataSet {
  @PrimaryGeneratedColumn('uuid')
  @Field(type => ID, { description: 'The unique identifier of the dataset' })
  readonly id: string

  @ManyToOne(type => Domain)
  @Field({ nullable: true, description: 'The domain to which the dataset belongs' })
  domain?: Domain

  @RelationId((dataSet: DataSet) => dataSet.domain)
  domainId?: string

  @Column()
  @Field({ description: 'The name of the dataset' })
  name?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'A description of the dataset' })
  description?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'A tag name of data to be published when a data sample created' })
  tag?: string

  @VersionColumn({ nullable: true, default: 1 })
  @Field({ nullable: true, description: 'The version of the dataset' })
  version?: number = 1

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Indicates whether the dataset is currently active or in use.' })
  active?: boolean

  @ManyToOne(type => DataKeySet, { nullable: true })
  @Field(type => DataKeySet, { nullable: true, description: 'The key set used for this dataset' })
  dataKeySet?: DataKeySet

  @RelationId((dataSet: DataSet) => dataSet.dataKeySet)
  dataKeySetId?: string

  @ManyToOne(type => Role, { nullable: true })
  @Field(type => Role, {
    nullable: true,
    description: 'The person responsible for manually entering the data for that dataset'
  })
  entryRole?: Role

  @RelationId((dataSet: DataSet) => dataSet.entryRole)
  entryRoleId?: string

  @ManyToOne(type => Role, { nullable: true })
  @Field(type => Role, { nullable: true, description: 'The final authority on issues related to that dataset.' })
  supervisoryRole?: Role

  @RelationId((dataSet: DataSet) => dataSet.supervisoryRole)
  supervisoryRoleId?: string

  @ManyToOne(type => Role, { nullable: true })
  @Field(type => Role, {
    nullable: true,
    description: 'The final authority on resolving outlier related to that dataset.'
  })
  resolverRole?: Role

  @RelationId((dataSet: DataSet) => dataSet.resolverRole)
  resolverRoleId?: string

  @Column('simple-json', { nullable: true })
  @Field(type => [AssigneeItem], { nullable: true, description: 'Who to contact for OOC issues' })
  assignees?: AssigneeItem[]

  @Column('simple-json', { nullable: true })
  @Field(type => [ApprovalLineItem], {
    nullable: true,
    description: 'Approval line for confrim of the dataset'
  })
  reviewApprovalLine?: ApprovalLineItem[]

  @Column('simple-json', { nullable: true })
  @Field(type => [ApprovalLineItem], {
    nullable: true,
    description: 'Approval line for handling abnormal data occurance issues for that dataset'
  })
  outlierApprovalLine?: ApprovalLineItem[]

  @ManyToOne(type => Scenario, { nullable: true })
  @Field(type => Scenario, {
    nullable: true,
    description: 'Scenario for handling normal data-sample occurance for that dataset'
  })
  normalScenario?: Scenario

  @RelationId((dataSet: DataSet) => dataSet.normalScenario)
  normalScenarioId?: string

  @ManyToOne(type => Scenario, { nullable: true })
  @Field(type => Scenario, {
    nullable: true,
    description: 'Scenario for handling abnormal data-sample occurance issues for that dataset'
  })
  outlierScenario?: Scenario

  @RelationId((dataSet: DataSet) => dataSet.outlierScenario)
  outlierScenarioId?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'The type of the dataset' })
  type?: 'manual' | 'automatic' | string

  @Column({ nullable: true })
  @Field({
    nullable: true,
    description: `Specifies the method of data entry for the dataset. If set to "generated", data entry screens are automatically generated based on the defined data items. For other types, specific entry screens are configured for each type and specified in the "entryView" field.`
  })
  entryType?: DataSetEntryType

  @Column({ nullable: true })
  @Field({
    nullable: true,
    description: `Specifies the custom data entry screen or view for the dataset. When "entryType" is not "generated", this field indicates the screen to be used for data entry. When "entryType" is "generated", this field may not apply.`
  })
  entryView?: string

  @Column({ nullable: true })
  @Field({
    nullable: true,
    description: `Specifies the method of data monitoring for the dataset. If set to "generated", monitoring screens are automatically generated based on the dataset configuration. For other types, specific monitoring screens are configured for each type and specified in the "monitorView" field.`
  })
  monitorType?: DataSetMonitorType

  @Column({ nullable: true })
  @Field({
    nullable: true,
    description: `Specifies the custom data monitoring screen or view for the dataset. When "monitorType" is not "generated", this field indicates the screen to be used for data monitoring. When "monitorType" is "generated", this field may not apply.`
  })
  monitorView?: string

  @Column({ nullable: true })
  @Field({
    nullable: true,
    description: `Specifies the method of reporting for the dataset. If set to "generated", report templates are automatically generated based on the dataset configuration. For other types, specific report templates are configured for each type and specified in the "reportView" field.`
  })
  reportType?: DataSetReportType

  @Column({ nullable: true })
  @Field({
    nullable: true,
    description: `Specifies the custom report template or view for the dataset. When "reportType" is not "generated", this field indicates the report template or view to be used for reporting. When "reportType" is "generated", this field may not apply.`
  })
  reportView?: string

  @Field({ nullable: true, description: 'The report template of the dataset' })
  reportTemplate?: string

  @Column({ nullable: true })
  @Field({
    nullable: true,
    description:
      'Specifies the intended purpose or role of the dataset within the system. It helps users understand why the dataset was created and guides the specification of data items accordingly. Common use case options may include "QA," "CCP," "SPC," and others.'
  })
  useCase?: string

  @Column('simple-json', { nullable: true })
  @Field(type => ScalarObject, {
    nullable: true,
    description:
      "Specifies the partition keys used to distribute and organize the dataset's data. Partition keys are properties or attributes that determine how data is logically grouped, stored, and retrieved. They enable efficient data management and querying by allowing data to be organized into smaller, manageable partitions or segments."
  })
  partitionKeys?: { [key: string]: any }

  @Column('simple-json', { nullable: true })
  @Field(type => [DataItem], { nullable: true, description: 'Data items for the dataset' })
  dataItems: DataItem[]

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'The schedule of the dataset' })
  schedule?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'The schedule ID of the dataset' })
  scheduleId?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'The timezone of the dataset' })
  timezone?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Indicates if the dataset requires review' })
  requiresReview?: boolean

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'The summary period of the dataset' })
  summaryPeriod?: DataSetSummaryPeriodType

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'The summary schedule of the dataset' })
  summarySchedule?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'The summary schedule ID of the dataset' })
  summaryScheduleId?: string

  @Column({ nullable: true })
  @Field({
    nullable: true,
    description: 'The maximum allowed time limit (in seconds) for completing the data entry operation'
  })
  timeLimit?: number

  @CreateDateColumn()
  @Field({ nullable: true, description: 'The date and time the dataset was created' })
  createdAt?: Date

  @UpdateDateColumn()
  @Field({ nullable: true, description: 'The date and time the dataset was last updated' })
  updatedAt?: Date

  @DeleteDateColumn()
  @Field({ nullable: true, description: 'The date and time the dataset was deleted' })
  deletedAt?: Date

  @ManyToOne(type => User, { nullable: true })
  @Field({ nullable: true, description: 'The user who created the dataset' })
  creator?: User

  @RelationId((dataSet: DataSet) => dataSet.creator)
  creatorId?: string

  @ManyToOne(type => User, { nullable: true })
  @Field({ nullable: true, description: 'The user who last updated the dataset' })
  updater?: User

  @RelationId((dataSet: DataSet) => dataSet.updater)
  updaterId?: string
}
